import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { getCheckoutStatus } from '@/services/api';
import { toast } from 'sonner';
import { Check, Loader2, Star, XCircle } from 'lucide-react';

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const pollPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      if (pollCount >= 10) {
        setStatus('error');
        toast.error('Payment verification timed out. Please contact support.');
        return;
      }

      try {
        const result = await getCheckoutStatus(sessionId);
        
        if (result.payment_status === 'paid') {
          setStatus('success');
          await refreshUser();
          toast.success('Subscription activated successfully!');
        } else if (result.status === 'expired') {
          setStatus('error');
          toast.error('Payment session expired');
        } else {
          // Continue polling
          setPollCount(prev => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollCount, refreshUser]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-stone-200 shadow-xl" data-testid="payment-processing-card">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-stone-600 animate-spin" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-stone-900 mb-2">
              Processing Payment
            </h1>
            <p className="text-stone-600">
              Please wait while we verify your payment...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-stone-200 shadow-xl" data-testid="payment-error-card">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-stone-900 mb-2">
              Payment Issue
            </h1>
            <p className="text-stone-600 mb-6">
              There was an issue verifying your payment. Please try again or contact support.
            </p>
            <div className="space-y-3">
              <Link to="/subscription">
                <Button className="w-full h-11 rounded-full bg-stone-900 text-white hover:bg-stone-800">
                  Try Again
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="outline" className="w-full h-11 rounded-full border-stone-200">
                  Go to Dashboard
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-stone-200 shadow-xl" data-testid="payment-success-card">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-[#5F8D4E]/10 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-[#5F8D4E]" />
          </div>
          <h1 className="font-heading text-3xl font-semibold text-stone-900 mb-2">
            Welcome to BeautyPro!
          </h1>
          <p className="text-stone-600 mb-8">
            Your subscription is now active. You have full access to all features.
          </p>
          
          <div className="bg-stone-50 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Star className="w-5 h-5 fill-[#D97757] text-[#D97757]" />
              <span className="font-heading text-lg font-semibold text-stone-900">Professional Plan</span>
            </div>
            <p className="text-stone-600 text-sm">$29.99/month • Renews automatically</p>
          </div>

          <Link to="/dashboard">
            <Button 
              className="w-full h-12 rounded-full bg-[#D97757] text-white hover:bg-[#C06040] text-base font-medium"
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
